import { useEffect, useState } from 'react'
import type { CharacterDetails, CharacterSummary } from '@rollcall/contracts'
import { useT } from './i18n-context.js'

/**
 * Имя и описание персонажа (D4).
 *
 * Поля в расчёт не идут и в журнал не пишутся: журнал хранит выборы,
 * а «шрам через бровь» выбором не является. Правит владелец персонажа
 * и мастер стола — те же двое, что двигают хиты, и проверка у сервера
 * одна на всех.
 *
 * Раздел отдельный, а не строка сверху: полей шесть, и каждое —
 * вопрос, на который человек однажды ответит. Пустое поле с подписью
 * «Слабость» этот вопрос задаёт; пустая простыня не задаёт ничего.
 */

/** Поля описания в том порядке, в каком их заполняют. */
export const DETAIL_FIELDS = [
  'appearance',
  'quirk',
  'motivation',
  'backstory',
  'weakness',
  'notes',
] as const

export type DetailField = (typeof DETAIL_FIELDS)[number]

/** Короткие поля пишутся строкой, длинные — абзацем. */
const LONG: ReadonlySet<DetailField> = new Set(['backstory', 'notes'])

export type DetailsSectionProps = {
  readonly character: CharacterSummary
  /** `undefined` — правки нет: лист открыт тем, кто не владеет им. */
  readonly onSave?:
    | ((patch: { name: string | null; details: CharacterDetails | null }) => void)
    | undefined
  readonly busy?: boolean | undefined
}

export function DetailsSection({
  character,
  onSave,
  busy = false,
}: DetailsSectionProps) {
  const t = useT()
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(character.name)
  const [details, setDetails] = useState<CharacterDetails>(character.details)

  // Лист перечитывается после каждой команды, и правка, сделанная
  // на другом устройстве, обязана доехать сюда. Пока поле открыто
  // на правку, набранное не затирается: иначе чужое сохранение
  // съело бы то, что человек ещё печатает.
  useEffect(() => {
    if (editing) return
    setName(character.name)
    setDetails(character.details)
  }, [editing, character.name, character.details])

  const filled = DETAIL_FIELDS.filter((field) => character.details[field] !== '')

  if (!editing) {
    return (
      <section aria-labelledby="sheet-details">
        <h3 id="sheet-details">{t('details.title')}</h3>
        {filled.length === 0 ? (
          <p className="app-slot-hint">{t('details.empty')}</p>
        ) : (
          <dl className="app-details" data-testid="details-view">
            {filled.map((field) => (
              <div key={field} className="app-detail">
                <dt>{t(`details.${field}`)}</dt>
                <dd>{character.details[field]}</dd>
              </div>
            ))}
          </dl>
        )}
        {onSave !== undefined && (
          <div className="app-actions">
            <button
              type="button"
              className="app-button"
              onClick={() => {
                setEditing(true)
              }}
              data-testid="details-edit"
            >
              {t('details.edit')}
            </button>
          </div>
        )}
      </section>
    )
  }

  return (
    <section aria-labelledby="sheet-details">
      <h3 id="sheet-details">{t('details.title')}</h3>
      <div className="app-form" data-testid="details-form">
        <label className="app-field">
          <span>{t('details.name')}</span>
          <input
            type="text"
            value={name}
            maxLength={60}
            onChange={(event) => {
              setName(event.target.value)
            }}
            data-testid="details-name"
          />
        </label>

        {DETAIL_FIELDS.map((field) => (
          <label key={field} className="app-field">
            <span>{t(`details.${field}`)}</span>
            {LONG.has(field) ? (
              <textarea
                rows={4}
                value={details[field]}
                maxLength={4000}
                onChange={(event) => {
                  setDetails({ ...details, [field]: event.target.value })
                }}
                data-testid={`details-${field}`}
              />
            ) : (
              <input
                type="text"
                value={details[field]}
                maxLength={2000}
                onChange={(event) => {
                  setDetails({ ...details, [field]: event.target.value })
                }}
                data-testid={`details-${field}`}
              />
            )}
          </label>
        ))}

        <div className="app-actions">
          <button
            type="button"
            className="app-button"
            disabled={busy || name.trim() === ''}
            onClick={() => {
              onSave?.({ name: name.trim(), details })
              setEditing(false)
            }}
            data-testid="details-save"
          >
            {t('details.save')}
          </button>
          <button
            type="button"
            className="app-button app-button-quiet"
            onClick={() => {
              setName(character.name)
              setDetails(character.details)
              setEditing(false)
            }}
          >
            {t('resources.cancel')}
          </button>
        </div>
      </div>
    </section>
  )
}
